import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AddressData } from '../../../../../models/patients/address';

@Component({
	selector: 'app-address-validation-dialog',
	templateUrl: './address-validation-dialog.component.html',
	styleUrls: ['./address-validation-dialog.component.scss']
})
export class AddressValidationDialogComponent {
	@Input() visible = false;
	@Input() scenario: 'found_different' | 'error' = 'error';
	@Input() originalAddress: AddressData | null = null;
	@Input() standardizedAddress: AddressData | null = null;
	@Input() errorMessage = '';
	@Input() isPartialInput = false;

	@Output() visibleChange = new EventEmitter<boolean>();
	@Output() useStandardized = new EventEmitter<AddressData>();
	@Output() useOriginal = new EventEmitter<void>();
	@Output() makeChanges = new EventEmitter<void>();

	selectedOption: 'standardized' | 'original' = 'standardized';

	/**
	 * DOVE-2545 QA — the title and icon are derived rather than sitting in three separate
	 * pTemplate="header" blocks behind *ngIf. p-dialog resolves its content templates once, so
	 * the first scenario to render kept its header forever: after a no-match, a later
	 * recommendation still read "Address Could Not Be Verified".
	 */
	get dialogTitle(): string {
		if (this.scenario === 'error') {
			return 'Address Could Not Be Verified';
		}

		return this.isPartialInput ? 'Address Found' : 'Verify Your Address';
	}

	get dialogIconClass(): string {
		return this.scenario === 'error'
			? 'pi pi-exclamation-triangle text-orange-500'
			: 'pi pi-map-marker text-blue-500';
	}

	onDialogHide(): void {
		this.visibleChange.emit(false);
		this.selectedOption = 'standardized';
	}

	onConfirm(): void {
		if (this.selectedOption === 'standardized' && this.standardizedAddress) {
			this.useStandardized.emit(this.toTitleCaseAddress(this.standardizedAddress));
		} else {
			this.useOriginal.emit();
		}
		this.onDialogHide();
	}

	onUseThisAddress(): void {
		if (this.standardizedAddress) {
			this.useStandardized.emit(this.toTitleCaseAddress(this.standardizedAddress));
		}
		this.onDialogHide();
	}

	onMakeChanges(): void {
		this.makeChanges.emit();
		this.onDialogHide();
	}

	onUseEntered(): void {
		this.useOriginal.emit();
		this.onDialogHide();
	}

	formatAddress(addr: AddressData | null): string {
		if (!addr) return '';
		const parts = [addr.address_1];
		if (addr.address_2) parts.push(addr.address_2);
		parts.push(`${addr.city}, ${addr.state} ${addr.zipcode}`);
		return parts.join(', ');
	}

	formatDisplayAddress(addr: AddressData | null): string {
		if (!addr) return '';
		return this.formatAddress(this.toTitleCaseAddress(addr));
	}

	private toTitleCase(str: string): string {
		return str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
	}

	private toTitleCaseAddress(addr: AddressData): AddressData {
		return {
			address_1: this.toTitleCase(addr.address_1),
			address_2: addr.address_2 ? this.toTitleCase(addr.address_2) : null,
			city: this.toTitleCase(addr.city),
			state: addr.state.toUpperCase(),
			zipcode: addr.zipcode
		};
	}
}
